import { baseApiAxios } from './js/portal/api';
import TileDonutChart from './jsx/components/tiles/TileDonutChart';
import TileText from './jsx/components/tiles/TileText';
import ChartHorizontalStacked from './jsx/components/tiles/ChartHorizontalStacked';
import ChartLegend from './jsx/components/tiles/ChartLegend';
import {
  defaultColors,
  loadDonutChart,
  loadLegendTile,
  loadOrderStatistics,
  loadSelectFieldBarChart,
  loadStackedAbsoluteValuesChart,
  loadTextTile,
  parseInitialTimeframeOptions,
  createTimeframeController,
  StatisticsPageHeader,
  withStatsLoading,
} from './js/portal/statistics';
import TileTable from './jsx/components/tiles/TileTable';
import { initDataTable, exportButtons, col } from './js/portal/datatable';

export default function StatisticsNetworkPage() {
  let usageData = {},
    typeData = {},
    cidrData = {},
    cidrsRequiredData = {};

  const controller = createTimeframeController({
    loadInitialData: async (radioSelection, dropdownSelection) => {
      await loadUsageData(radioSelection, dropdownSelection);
      return parseInitialTimeframeOptions(usageData?.stats, radioSelection);
    },
    loadEmptyTiles,
    loadDashboardData,
    getStatsData: () => usageData,
  });

  const page = (
    <div id="dashboard_overview_network">
      <StatisticsPageHeader
        title="Network Statistics"
        dropdownChangeListener={controller.dropdownChangeListener}
        radioChangeListener={controller.radioChangeListener}
      />
      <div class="row">
        <TileText id="number-vpcs" title="VPCs Total"></TileText>
        <TileText id="number-private-vpcs" title="Private VPCs"></TileText>
        <TileText id="number-public-vpcs" title="Public VPCs"></TileText>
        <TileDonutChart
          id="vpcs-by-type"
          title="VPCs by Type"
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileText id="vpcs-by-region-and-type" title="Number of VPCs" headline="by Region and Type">
          <ChartHorizontalStacked
            title=""
            hideLegend={true}
            values={[]}
            colors={defaultColors}></ChartHorizontalStacked>
          <ChartLegend labels={[]} colors={defaultColors}></ChartLegend>
        </TileText>
        <TileText id="regions-need-cidrs" title="CIDRs needed" headline="Regions running out of CIDRs">
          <ChartLegend labels={[]} colors={defaultColors} minHeight={'120px'} largeText={true}></ChartLegend>
        </TileText>
        <TileText id="vpcs-by-region-and-size" title="Number of VPCs" headline="by Region and Size" collapse={true}>
          <ChartHorizontalStacked
            title=""
            hideLegend={true}
            values={[]}
            colors={defaultColors}></ChartHorizontalStacked>
          <ChartLegend labels={[]} colors={defaultColors}></ChartLegend>
        </TileText>
        <TileDonutChart
          id="vpcs-by-size"
          title="VPCs by Size"
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileText id="free-cidrs-by-region-size" title="Free CIDRs" headline="by Region and Size">
          <ChartHorizontalStacked title="" hideLegend={true} values={[]} colors={[]}></ChartHorizontalStacked>
          <ChartLegend labels={[]} colors={[]}></ChartLegend>
        </TileText>
        <TileText id="create-vpc" title="Create VPC"></TileText>
        <TileText id="delete-vpc" title="Delete VPC"></TileText>
        <TileText id="create-4wheels" title="Create 4Wheels"></TileText>
        <TileTable id="vpc-data"></TileTable>
      </div>
    </div>
  );

  async function onPageReady() {
    controller.onPageReady();
  }

  // LOAD AND DISPLAY DATA

  function loadEmptyTiles() {
    loadTextTile('number-vpcs', '-', null, true);
    loadTextTile('number-private-vpcs', '-', null, true);
    loadTextTile('number-public-vpcs', '-', null, true);
    loadTextTile('create-vpc', '-', null, true);
    loadTextTile('delete-vpc', '-', null, true);
    loadTextTile('create-4wheels', '-', null, true);
    loadSelectFieldBarChart('free-cidrs-by-region-size', 'Free CIDRs', 'by Region and Size', {}, '-', false);
    loadStackedAbsoluteValuesChart(
      'vpcs-by-region-and-size',
      'Number of VPCs',
      'by Region and Size',
      {},
      [],
      10,
      true,
      true,
    );
    loadDonutChart('vpcs-by-size', 'VPCs by Size', {});
    loadStackedAbsoluteValuesChart(
      'vpcs-by-region-and-type',
      'Number of VPCs',
      'by Region and Type',
      {},
      [],
      10,
      true,
      true,
    );
    loadDonutChart('vpcs-by-type', 'VPCs by Type', {});
    loadLegendTile('regions-need-cidrs', 'CIDRs needed', 'Regions running out of CIDRs', []);
    $('#table-vpc-data').DataTable().clear().draw();
  }

  function loadDashboardData(timeframe, sk) {
    loadOrderStatistics('create-vpc', 'orders', 'create-vpc', 'monthly', sk);
    loadOrderStatistics('delete-vpc', 'orders', 'destroy-vpc', 'monthly', sk);
    loadOrderStatistics('create-4wheels', 'orders', 'create-4wheels-cluster', 'monthly', sk);

    loadUsageData(timeframe, sk);
    loadTypeData(timeframe, sk);
    loadCidrData(timeframe, sk);
    loadCidrsRequiredData(timeframe, sk);
  }

  async function _fetchUsageData(timeframe, sk) {
    usageData = await baseApiAxios.get(
      `/stats/categories/vpc/statistics/usage?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
      null,
      {},
    );

    if (usageData?.stats?.length) {
      let dataObj = usageData.stats[0].data;
      delete dataObj['count'];
      let chartData = {};
      let countRegions = {};
      let sizeObject = {};

      Object.entries(dataObj).forEach(([size, data]) => {
        chartData[`/${size}`] = data;
        let regionData = data;
        sizeObject[`/${size}`] = data.count;
        Object.entries(regionData)
          .filter(item => item[0] !== 'count')
          .forEach(([region, values]) => {
            countRegions[region] = (countRegions[region] || 0) + values.count;
          });
      });

      loadStackedAbsoluteValuesChart(
        'vpcs-by-region-and-size',
        'Number of VPCs',
        'by Region and Size',
        chartData,
        Object.keys(countRegions),
        Math.max(...Object.values(sizeObject)),
        true,
        true,
      );
      loadDonutChart('vpcs-by-size', 'VPCs by Size', sizeObject);
    }
  }

  async function loadUsageData(timeframe, sk) {
    await withStatsLoading(() => _fetchUsageData(timeframe, sk));
  }

  async function loadTypeData(timeframe, sk) {
    await withStatsLoading(async () => {
      typeData = await baseApiAxios.get(
        `/stats/categories/vpc/statistics/network_type?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
        null,
        {},
      );

      if (typeData?.stats?.length) {
        let chartData = typeData.stats[0].data;
        loadTextTile('number-vpcs', chartData.count, null, true);
        loadTextTile('number-private-vpcs', chartData.private?.count, null, true);
        loadTextTile('number-public-vpcs', chartData.public?.count, null, true);

        delete chartData['count'];
        let vpcRegions = [];
        let nrVpcsByType = {};

        Object.entries(chartData).forEach(([type, regionData]) => {
          vpcRegions = vpcRegions.concat(Object.keys(regionData));
          nrVpcsByType[type] = regionData.count;
        });

        loadStackedAbsoluteValuesChart(
          'vpcs-by-region-and-type',
          'Number of VPCs',
          'by Region and Type',
          chartData,
          vpcRegions.filter((value, index, self) => self.indexOf(value) === index),
          Math.max(...Object.values(nrVpcsByType)),
          true,
          true,
        );
        loadDonutChart('vpcs-by-type', 'VPCs by Type', nrVpcsByType);

        loadVpcTable();
      }
    });
  }

  async function loadCidrData(timeframe, sk) {
    await withStatsLoading(async () => {
      cidrData = await baseApiAxios.get(
        `/stats/categories/cidr/statistics/free_cidrs?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
        null,
        {},
      );

      if (cidrData?.stats?.length) {
        loadSelectFieldBarChart(
          'free-cidrs-by-region-size',
          'Free CIDRs',
          'by Region and Size',
          cidrData.stats[0].data,
          '25',
          false,
        );
      }
    });
  }

  async function loadCidrsRequiredData(timeframe, sk) {
    await withStatsLoading(async () => {
      cidrsRequiredData = await baseApiAxios.get(
        `/stats/categories/cidr/statistics/regions_need_cidr?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
        null,
        {},
      );

      let regions = [];
      if (cidrsRequiredData?.stats?.length) {
        Object.entries(cidrsRequiredData.stats[0].data).forEach(([region, value]) => {
          if (value) regions.push(region);
        });

        loadLegendTile('regions-need-cidrs', 'CIDRs needed', 'Regions running out of CIDRs', regions);
      }
    });
  }

  function _buildRegionRow(tableDataObj, region, type, values) {
    if (!tableDataObj[region]) tableDataObj[region] = {};
    tableDataObj[region][type] = values.count;
    tableDataObj[region]['count'] = (tableDataObj[region]['count'] || 0) + values.count;
    for (const [size, nr] of Object.entries(values)) {
      if (size === 'count') continue;
      tableDataObj[region][size] = (tableDataObj[region][size] || 0) + nr;
    }
  }

  async function _fetchVpcTableData() {
    let chartDataObj = typeData.stats[0].data;
    let tableDataObj = {};

    for (const [type, regionData] of Object.entries(chartDataObj)) {
      for (const [region, values] of Object.entries(regionData)) {
        if (region === 'count') continue;
        _buildRegionRow(tableDataObj, region, type, values);
      }
    }

    const tableData = Object.entries(tableDataObj).map(([key, dataset]) => Object.assign({ region: key }, dataset));

    initDataTable(
      'table-vpc-data',
      exportButtons({ copy: false }),
      [
        col('region', 'region_col', 'Region'),
        col('count', 'count_col', 'Count'),
        col('special', 'special_col', 'Special'),
        col('external', 'external_col', 'External'),
        col('private', 'private_col', 'Private'),
        col('public', 'public_col', 'Public'),
        col('unknown', 'unknown_col', 'Unknown'),
        ...[...Array(9).keys()].map(key => {
          const size = key + 20;
          return col(`${size}`, `${size}_col`, `Size ${size}`, { visible: false });
        }),
      ],
      null,
      null,
    );

    const dt = $('#table-vpc-data').DataTable();
    dt.rows().remove();
    dt.rows.add(tableData).draw(false);
  }

  async function loadVpcTable() {
    await withStatsLoading(() => _fetchVpcTableData());
  }

  return [page, onPageReady];
}
